import { Suspense, useState } from "react";
import { useLocation, useNavigate } from "@tanstack/react-router";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { XIcon } from "lucide-react";
import { findAnnouncement, type AnnouncementId } from "@brewbase/lib";
import { isQuietPath } from "./announcement-moment";
import { ANNOUNCEMENT_CONTENT } from "./announcement-content";
import { useAnnouncementStore } from "./announcement-store";
import { useAnnouncementClaim } from "./use-announcement-claim";

/**
 * Mounted once in the app shell for signed-in operators. Shows the
 * announcement this load claimed (at most one, at most once per operator),
 * or one opened by hand. Closing is always one action: Escape, the backdrop,
 * the close button or the call to action.
 */
export function AnnouncementHost() {
  const pathname = useLocation({ select: (location) => location.pathname });
  const manualId = useAnnouncementStore((state) => state.openId);
  const closeManual = useAnnouncementStore((state) => state.close);
  const claim = useAnnouncementClaim(!isQuietPath(pathname));
  const [claimClosed, setClaimClosed] = useState(false);

  const activeId = manualId ?? (claimClosed ? null : (claim.data ?? null));
  if (!activeId) return null;

  const close = () => {
    if (manualId) closeManual();
    else setClaimClosed(true);
  };
  return <AnnouncementDialog key={activeId} id={activeId} onClose={close} />;
}

/**
 * The one sanctioned popup outside the dialog rules: a dimmed, blurred
 * backdrop and the content centred at 16:9, as large as the window allows.
 */
function AnnouncementDialog({
  id,
  onClose,
}: {
  id: AnnouncementId;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const Content = ANNOUNCEMENT_CONTENT[id];
  const title = findAnnouncement(id)?.title ?? "What's new";

  return (
    <DialogPrimitive.Root open onOpenChange={(open) => !open && onClose()}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-100 bg-black/70 backdrop-blur-[2px] duration-500 data-[state=open]:animate-in data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          aria-describedby={undefined}
          className="fixed left-1/2 top-1/2 z-100 w-[min(92vw,calc(84vh*16/9),1120px)] -translate-x-1/2 -translate-y-1/2 outline-none duration-500 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95"
        >
          <DialogPrimitive.Title className="sr-only">{title}</DialogPrimitive.Title>
          <DialogPrimitive.Close
            className="absolute -top-9 right-0 flex items-center gap-1.5 text-sm text-white/70 hover:text-white focus:outline-none focus-visible:text-white"
          >
            Close
            <XIcon className="size-4" />
          </DialogPrimitive.Close>
          <Suspense
            fallback={<div className="aspect-video w-full bg-olive-1 shadow-2xl" />}
          >
            <Content
              onClose={onClose}
              onNavigate={(to) => {
                onClose();
                void navigate({ to });
              }}
            />
          </Suspense>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
